import React, { useState, useEffect, useMemo } from 'react';
import { 
  Table, Card, Typography, Tag, Spin, Row, Col, Statistic, 
  DatePicker, Select, Button, Space, Tabs, Progress, Divider,
  Modal, Alert, Empty
} from 'antd';
import { 
  DownloadOutlined, FilterOutlined, CalendarOutlined, 
  BarChartOutlined, DollarOutlined, UserOutlined,
  ReloadOutlined, ExportOutlined, EyeOutlined
} from '@ant-design/icons';
import axios from 'axios';
import moment from 'moment';
import * as XLSX from 'xlsx';

const { Title, Text, Paragraph } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;
const { TabPane } = Tabs;

const API_URL = {
  sales: 'https://agpipefitting.org.et/api/sales',
  products: 'https://agpipefitting.org.et/api/products'
};

const SalesListAndReport = () => {
  // State Management
  const [sales, setSales] = useState([]);
  const [filteredSales, setFilteredSales] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [dateRange, setDateRange] = useState(null);
  const [salesTypeFilter, setSalesTypeFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('overview');
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // Initial Data Fetch
  useEffect(() => {
    fetchData();
  }, []);

  // Filter sales when criteria change
  useEffect(() => {
    filterSales();
  }, [sales, dateRange, salesTypeFilter]);

  // Fetch data from API
  const fetchData = async () => {
    setLoading(true);
    try {
      const [salesRes, productsRes] = await Promise.all([
        axios.get(API_URL.sales),
        axios.get(API_URL.products)
      ]);

      const processedSales = salesRes.data.map((sale) => ({
        ...sale,
        id: sale.id || sale.sale_id,
        sale_date: moment(sale.sale_date),
        total_price: Number(sale.total_price) || 0,
        formatted_date: moment(sale.sale_date).format('YYYY-MM-DD')
      }));

      setSales(processedSales);
      setFilteredSales(processedSales);
      setProducts(productsRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  };

  // Apply filters to sales data
  const filterSales = () => {
    let filtered = [...sales];

    // Apply date range filter
    if (dateRange && dateRange[0] && dateRange[1]) {
      filtered = filtered.filter(sale => {
        const saleDate = moment(sale.formatted_date);
        return saleDate.isBetween(dateRange[0], dateRange[1], 'day', '[]');
      });
    }

    // Apply sales type filter
    if (salesTypeFilter !== 'all') {
      filtered = filtered.filter(sale => sale.salesType === salesTypeFilter);
    }

    setFilteredSales(filtered);
  };

  // Generate comprehensive reports
  const reportData = useMemo(() => {
    let totalSales = 0;
    let totalTransactions = filteredSales.length;
    const salesByType = { Cash: 0, Credit: 0, Gift: 0 };
    const salesByProduct = {};
    const customerMap = {};

    filteredSales.forEach(sale => {
      const total = Number(sale.total_price) || 0;
      totalSales += total;
      
      // Sales by type
      if (sale.salesType) {
        salesByType[sale.salesType] = (salesByType[sale.salesType] || 0) + total;
      }

      // Sales by product
      const productId = sale.product_id;
      if (productId) {
        if (!salesByProduct[productId]) {
          salesByProduct[productId] = {
            id: productId,
            quantity: 0,
            revenue: 0
          };
        }
        salesByProduct[productId].quantity += Number(sale.quantity) || 0;
        salesByProduct[productId].revenue += total;
      }

      // Customer data
      const customerName = sale.customer_name || 'Unknown Customer';
      if (!customerMap[customerName]) {
        customerMap[customerName] = {
          name: customerName,
          total: 0,
          transactions: 0,
          sales: []
        };
      }
      customerMap[customerName].total += total;
      customerMap[customerName].transactions += 1;
      customerMap[customerName].sales.push(sale);
    });

    // Top customers
    const topCustomers = Object.values(customerMap)
      .sort((a, b) => b.total - a.total)
      .slice(0, 5);

    // Top products
    const topProducts = Object.values(salesByProduct)
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 5)
      .map(item => ({
        ...item,
        product: products.find(p => p.id === item.id) || {}
      }));

    // Daily averages
    const uniqueDays = [...new Set(filteredSales.map(s => s.formatted_date))].length;
    const avgDailySales = uniqueDays > 0 ? totalSales / uniqueDays : 0;

    return {
      totalSales,
      totalTransactions,
      salesByType,
      topCustomers,
      topProducts,
      avgDailySales,
      uniqueCustomers: Object.keys(customerMap).length
    };
  }, [filteredSales, products]);

  // Export functionality
  const exportToExcel = (data, fileName) => {
    setExporting(true);
    try {
      const worksheet = XLSX.utils.json_to_sheet(data);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Sales Data');
      XLSX.writeFile(workbook, `${fileName}_${moment().format('YYYY-MM-DD')}.xlsx`);
    } catch (error) {
      console.error('Export error:', error);
      alert('Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  const handleExport = () => {
    const exportData = filteredSales.map(sale => ({
      Date: sale.formatted_date,
      'Customer Name': sale.customer_name,
      'Reference No': sale.referenceNo,
      'Product Type': products.find(p => p.id === sale.product_id)?.type || 'N/A',
      'Product Size': products.find(p => p.id === sale.product_id)?.size || 'N/A',
      'Sales Type': sale.salesType,
      Quantity: sale.quantity,
      'Unit Price': (Number(sale.total_price) / Number(sale.quantity)).toFixed(2),
      'Total Price': sale.total_price.toFixed(2)
    }));
    exportToExcel(exportData, 'Sales_Report');
  };

  // Reset filters
  const resetFilters = () => {
    setDateRange(null);
    setSalesTypeFilter('all');
  };

  // Table columns for customer sales
  const customerColumns = [
    {
      title: 'Customer',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Button type="link" onClick={() => {
          setSelectedCustomer(record);
          setModalVisible(true);
        }}>
          <UserOutlined /> {text}
        </Button>
      ),
      sorter: (a, b) => a.name.localeCompare(b.name)
    },
    {
      title: 'Total Sales',
      dataIndex: 'total',
      key: 'total',
      render: (value) => (
        <Text strong style={{ color: '#1890ff' }}>
          ETB {value.toFixed(2)}
        </Text>
      ),
      sorter: (a, b) => a.total - b.total
    },
    {
      title: 'Transactions',
      dataIndex: 'transactions',
      key: 'transactions',
      render: (value) => <Tag color="blue">{value}</Tag>
    },
    {
      title: 'Avg. Transaction',
      key: 'avg',
      render: (_, record) => (
        <Text>ETB {(record.total / record.transactions).toFixed(2)}</Text>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Button 
          size="small" 
          icon={<EyeOutlined />}
          onClick={() => {
            setSelectedCustomer(record);
            setModalVisible(true);
          }}
        >
          Details
        </Button>
      )
    }
  ];

  // Product performance columns
  const productColumns = [
    {
      title: 'Product',
      key: 'product',
      render: (_, record) => {
        const product = record.product;
        return (
          <div>
            <Text strong>{product.type || 'Unknown'}</Text>
            <br />
            <Text type="secondary">Size: {product.size || 'N/A'}</Text>
          </div>
        );
      }
    },
    {
      title: 'Quantity Sold',
      dataIndex: 'quantity',
      key: 'quantity',
      sorter: (a, b) => a.quantity - b.quantity
    },
    {
      title: 'Revenue',
      dataIndex: 'revenue',
      key: 'revenue',
      render: (value) => (
        <Text strong style={{ color: '#52c41a' }}>
          ETB {value.toFixed(2)}
        </Text>
      ),
      sorter: (a, b) => a.revenue - b.revenue
    },
    {
      title: 'Performance',
      key: 'performance',
      render: (_, record) => {
        const maxRevenue = Math.max(...reportData.topProducts.map(p => p.revenue));
        const percentage = (record.revenue / maxRevenue) * 100;
        return (
          <Progress 
            percent={Math.round(percentage)} 
            size="small" 
            strokeColor={percentage > 70 ? '#52c41a' : percentage > 30 ? '#faad14' : '#ff4d4f'}
          />
        );
      }
    }
  ];

  // Modal columns for customer details
  const customerDetailColumns = [
    {
      title: 'Date',
      dataIndex: 'formatted_date',
      key: 'date'
    },
    {
      title: 'Reference',
      dataIndex: 'referenceNo',
      key: 'reference'
    },
    {
      title: 'Product',
      key: 'product',
      render: (_, sale) => {
        const product = products.find(p => p.id === sale.product_id);
        return product ? `${product.type} (${product.size})` : 'N/A';
      }
    },
    {
      title: 'Type',
      dataIndex: 'salesType',
      key: 'type',
      render: (type) => {
        const colors = { Cash: 'green', Credit: 'orange', Gift: 'blue' };
        return <Tag color={colors[type] || 'default'}>{type}</Tag>;
      }
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      key: 'quantity'
    },
    {
      title: 'Total',
      dataIndex: 'total_price',
      key: 'total',
      render: (value) => `ETB ${Number(value).toFixed(2)}`
    }
  ];

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-6">
        <Row justify="space-between" align="middle" gutter={[16, 16]}>
          <Col>
            <Title level={2} className="mb-1">
              <DollarOutlined className="mr-2" />
              Sales Dashboard
            </Title>
            <Text type="secondary">
              Comprehensive sales tracking and reporting system
            </Text>
          </Col>
          <Col>
            <Space>
              <Button 
                icon={<ReloadOutlined />} 
                onClick={fetchData}
                loading={loading}
              >
                Refresh
              </Button>
              <Button 
                type="primary" 
                icon={<ExportOutlined />}
                onClick={handleExport}
                loading={exporting}
              >
                Export Report
              </Button>
            </Space>
          </Col>
        </Row>
      </div>

      {/* Filter Section */}
      <Card className="shadow-lg mb-6" bordered={false}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} md={8}>
            <div>
              <Text strong className="block mb-2">
                <CalendarOutlined className="mr-2" />
                Date Range
              </Text>
              <RangePicker
                style={{ width: '100%' }}
                value={dateRange}
                onChange={setDateRange}
                format="YYYY-MM-DD"
              />
            </div>
          </Col>
          <Col xs={24} md={6}>
            <div>
              <Text strong className="block mb-2">
                <FilterOutlined className="mr-2" />
                Sales Type
              </Text>
              <Select
                style={{ width: '100%' }}
                value={salesTypeFilter}
                onChange={setSalesTypeFilter}
              >
                <Option value="all">All Types</Option>
                <Option value="Cash">Cash</Option>
                <Option value="Credit">Credit</Option>
                <Option value="Gift">Gift</Option>
              </Select>
            </div>
          </Col>
          <Col xs={24} md={10}>
            <div className="flex flex-col h-full justify-end">
              <Space>
                <Button onClick={resetFilters}>
                  Clear Filters
                </Button>
                <Text type="secondary">
                  Showing {filteredSales.length} of {sales.length} transactions
                </Text>
              </Space>
            </div>
          </Col>
        </Row>
      </Card>

      {/* Summary Statistics */}
      <Row gutter={[16, 16]} className="mb-6">
        <Col xs={24} sm={12} lg={6}>
          <Card className="shadow-md" bordered={false}>
            <Statistic
              title="Total Sales"
              value={reportData.totalSales}
              prefix="ETB"
              precision={2}
              valueStyle={{ color: '#1890ff' }}
            />
            <Progress 
              percent={100} 
              showInfo={false}
              strokeColor="#1890ff"
              size="small"
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card className="shadow-md" bordered={false}>
            <Statistic
              title="Transactions"
              value={reportData.totalTransactions}
              valueStyle={{ color: '#52c41a' }}
            />
            <Text type="secondary">Avg: {(reportData.totalTransactions / reportData.uniqueDays || 0).toFixed(1)}/day</Text>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card className="shadow-md" bordered={false}>
            <Statistic
              title="Unique Customers"
              value={reportData.uniqueCustomers}
              valueStyle={{ color: '#722ed1' }}
            />
            <Progress 
              percent={Math.min((reportData.uniqueCustomers / 50) * 100, 100)} 
              showInfo={false}
              strokeColor="#722ed1"
              size="small"
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card className="shadow-md" bordered={false}>
            <Statistic
              title="Avg. Daily Sales"
              value={reportData.avgDailySales}
              prefix="ETB"
              precision={2}
              valueStyle={{ color: '#fa8c16' }}
            />
            <Text type="secondary">{reportData.uniqueDays} days period</Text>
          </Card>
        </Col>
      </Row>

      {/* Sales Type Breakdown */}
      <Row gutter={[16, 16]} className="mb-6">
        <Col xs={24} lg={8}>
          <Card 
            title={<><BarChartOutlined className="mr-2" />Sales by Type</>} 
            className="shadow-lg"
            bordered={false}
          >
            {Object.entries(reportData.salesByType).map(([type, amount]) => {
              const percentage = reportData.totalSales > 0 ? (amount / reportData.totalSales) * 100 : 0;
              return (
                <div key={type} className="mb-4">
                  <div className="flex justify-between mb-1">
                    <Text>{type}</Text>
                    <Text strong>ETB {amount.toFixed(2)}</Text>
                  </div>
                  <Progress 
                    percent={percentage.toFixed(1)} 
                    strokeColor={
                      type === 'Cash' ? '#52c41a' : 
                      type === 'Credit' ? '#faad14' : '#1890ff'
                    }
                  />
                </div>
              );
            })}
          </Card>
        </Col>
        <Col xs={24} lg={16}>
          <Card 
            title="Sales Analytics"
            className="shadow-lg"
            bordered={false}
            extra={
              <Tabs activeKey={activeTab} onChange={setActiveTab} size="small">
                <TabPane tab="Top Customers" key="customers" />
                <TabPane tab="Top Products" key="products" />
              </Tabs>
            }
          >
            <Spin spinning={loading}>
              {activeTab === 'customers' ? (
                <Table
                  columns={customerColumns}
                  dataSource={reportData.topCustomers}
                  rowKey="name"
                  pagination={false}
                  size="middle"
                />
              ) : (
                <Table
                  columns={productColumns}
                  dataSource={reportData.topProducts}
                  rowKey="id"
                  pagination={false}
                  size="middle"
                />
              )}
            </Spin>
          </Card>
        </Col>
      </Row>

      {/* Detailed Transactions */}
      <Card 
        title={<><DollarOutlined className="mr-2" />Transaction Details</>}
        className="shadow-lg"
        bordered={false}
        extra={
          <Button 
            type="primary" 
            ghost 
            onClick={handleExport}
            loading={exporting}
          >
            <DownloadOutlined /> Export Current View
          </Button>
        }
      >
        <Spin spinning={loading}>
          {filteredSales.length === 0 ? (
            <Empty description="No transactions found for the selected filters" />
          ) : (
            <Table
              dataSource={filteredSales}
              rowKey="id"
              scroll={{ x: true }}
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} transactions`
              }}
              expandable={{
                expandedRowRender: (record) => (
                  <div className="p-4 bg-gray-50">
                    <Text strong>Details</Text>
                    <Row gutter={16} className="mt-2">
                      <Col span={8}>
                        <Text type="secondary">Product: </Text>
                        <Text>
                          {products.find(p => p.id === record.product_id)?.type || 'N/A'} 
                          ({products.find(p => p.id === record.product_id)?.size || 'N/A'})
                        </Text>
                      </Col>
                      <Col span={8}>
                        <Text type="secondary">Unit Price: </Text>
                        <Text>
                          ETB {((record.total_price || 0) / (record.quantity || 1)).toFixed(2)}
                        </Text>
                      </Col>
                      <Col span={8}>
                        <Text type="secondary">Date: </Text>
                        <Text>{record.formatted_date}</Text>
                      </Col>
                    </Row>
                  </div>
                )
              }}
              columns={[
                {
                  title: 'Date',
                  dataIndex: 'formatted_date',
                  key: 'date',
                  sorter: (a, b) => moment(a.formatted_date).unix() - moment(b.formatted_date).unix()
                },
                {
                  title: 'Customer',
                  dataIndex: 'customer_name',
                  key: 'customer',
                  render: (text) => <Text strong>{text}</Text>
                },
                {
                  title: 'Reference',
                  dataIndex: 'referenceNo',
                  key: 'reference'
                },
                {
                  title: 'Type',
                  dataIndex: 'salesType',
                  key: 'type',
                  render: (type) => (
                    <Tag color={type === 'Cash' ? 'green' : type === 'Credit' ? 'orange' : 'blue'}>
                      {type}
                    </Tag>
                  )
                },
                {
                  title: 'Quantity',
                  dataIndex: 'quantity',
                  key: 'quantity',
                  align: 'right'
                },
                {
                  title: 'Total Amount',
                  dataIndex: 'total_price',
                  key: 'total',
                  align: 'right',
                  render: (value) => (
                    <Text strong style={{ color: '#1890ff' }}>
                      ETB {Number(value).toFixed(2)}
                    </Text>
                  ),
                  sorter: (a, b) => a.total_price - b.total_price
                }
              ]}
            />
          )}
        </Spin>
      </Card>

      {/* Customer Details Modal */}
      <Modal
        title={
          <div>
            <UserOutlined className="mr-2" />
            Customer Details: {selectedCustomer?.name}
          </div>
        }
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={800}
      >
        {selectedCustomer && (
          <>
            <Row gutter={16} className="mb-6">
              <Col span={12}>
                <Statistic
                  title="Total Spend"
                  value={selectedCustomer.total}
                  prefix="ETB"
                  precision={2}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Total Transactions"
                  value={selectedCustomer.transactions}
                />
              </Col>
            </Row>
            <Divider />
            <Title level={5}>Transaction History</Title>
            <Table
              columns={customerDetailColumns}
              dataSource={selectedCustomer.sales}
              rowKey="id"
              pagination={{ pageSize: 5 }}
              size="small"
            />
          </>
        )}
      </Modal>

      {/* Footer Note */}
      <div className="mt-6 text-center">
        <Text type="secondary">
          Last updated: {moment().format('YYYY-MM-DD HH:mm')} | 
          Data refreshes automatically | 
          Report period: {dateRange ? 
            `${dateRange[0].format('MMM DD')} - ${dateRange[1].format('MMM DD, YYYY')}` : 
            'All time'
          }
        </Text>
      </div>
    </div>
  );
};

export default SalesListAndReport;